import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/landing/Logo";
import { useAuth } from "@/contexts/AuthContext";
import {
  adminListPosts, adminCreatePost, adminDeletePost, adminDuplicate,
  adminPublish, adminUnpublish, adminArchive, adminAnalytics, absoluteUrl,
} from "@/lib/blog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Search, Loader2, Copy, Trash2, Eye, EyeOff, Archive,
  Calendar, MoreHorizontal, BarChart3, FileText, Tag as TagIcon, Folder, LayoutGrid,
} from "lucide-react";

const STATUS_STYLES = {
  draft:      "bg-slate-100 text-slate-700 ring-slate-200",
  published:  "bg-emerald-100 text-emerald-700 ring-emerald-200",
  scheduled:  "bg-blue-100 text-blue-700 ring-blue-200",
  archived:   "bg-amber-100 text-amber-700 ring-amber-200",
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

export default function AdminBlog() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [posts, setPosts] = useState({ items: [], total: 0, total_pages: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftQ, setDraftQ] = useState(params.get("q") || "");
  const status = params.get("status") || "";
  const q = params.get("q") || "";
  const page = parseInt(params.get("page") || "1", 10);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        adminListPosts({ q: q || undefined, status: status || undefined, page, page_size: 15 }),
        adminAnalytics(),
      ]);
      setPosts(p);
      setAnalytics(a);
    } catch (err) {
      toast.error("Could not load blog dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [q, status, page]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v == null || v === "") next.delete(k); else next.set(k, v); });
    setParams(next, { replace: false });
  };

  const create = async () => {
    const draft = await adminCreatePost({ title: "Untitled post", status: "draft" });
    nav(`/admin/blog/${draft.id}/edit`);
  };

  const doPublish = async (id) => { await adminPublish(id); toast.success("Published"); load(); };
  const doUnpublish = async (id) => { await adminUnpublish(id); toast.success("Reverted to draft"); load(); };
  const doArchive = async (id) => { await adminArchive(id); toast.success("Archived"); load(); };
  const doDuplicate = async (id) => { const d = await adminDuplicate(id); toast.success("Duplicated"); nav(`/admin/blog/${d.id}/edit`); };
  const doDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}" — this cannot be undone.`)) return;
    await adminDeletePost(id);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-blog-page">
      <header className="bg-white/70 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="inline-flex items-center text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-slate-900 text-white">Blog</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => nav("/admin")} className="rounded-full" data-testid="admin-blog-back"><ArrowLeft className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Dashboard</span></Button>
            <Link to="/admin/blog/categories" className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 sm:px-3 py-2 rounded-full hover:bg-slate-50 hidden sm:block" data-testid="admin-blog-cats-link">Categories</Link>
            <Link to="/admin/blog/tags" className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 sm:px-3 py-2 rounded-full hover:bg-slate-50 hidden sm:block" data-testid="admin-blog-tags-link">Tags</Link>
            <Link to="/admin/blog/authors" className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 sm:px-3 py-2 rounded-full hover:bg-slate-50 hidden sm:block" data-testid="admin-blog-authors-link">Authors</Link>
            <Button onClick={create} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="admin-blog-new"><Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">New post</span></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-6">
        {/* Analytics tiles */}
        {analytics && (
          <section className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="admin-blog-analytics">
            {[
              { icon: FileText,   label: "Posts",       value: analytics.totals.posts_total,     tone: "slate" },
              { icon: Eye,        label: "Published",   value: analytics.totals.posts_published, tone: "emerald" },
              { icon: EyeOff,     label: "Drafts",      value: analytics.totals.posts_draft,     tone: "amber" },
              { icon: Calendar,   label: "Scheduled",   value: analytics.totals.posts_scheduled, tone: "blue" },
              { icon: Folder,     label: "Categories",  value: analytics.totals.categories,      tone: "violet" },
              { icon: BarChart3,  label: "Views 7d",    value: analytics.views_last_7_days,      tone: "rose" },
            ].map((t, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`blog-tile-${t.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className={`h-9 w-9 rounded-xl bg-${t.tone}-50 text-${t.tone}-600 flex items-center justify-center mb-2`}>
                  <t.icon className="h-4 w-4" />
                </div>
                <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{t.value}</div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{t.label}</div>
              </div>
            ))}
          </section>
        )}

        {/* Filters */}
        <section className="rounded-2xl bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <form onSubmit={(e) => { e.preventDefault(); update({ q: draftQ, page: null }); }}>
              <Input value={draftQ} onChange={(e) => setDraftQ(e.target.value)} placeholder="Search posts…" className="pl-9 h-10" data-testid="admin-blog-search" />
            </form>
          </div>
          <div className="flex items-center gap-1.5" data-testid="admin-blog-status-filter">
            {["", "draft", "published", "scheduled", "archived"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => update({ status: s, page: null })}
                data-testid={`admin-blog-status-${s || "all"}`}
                className={`px-3 h-9 rounded-full text-xs font-semibold capitalize ${status === s ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
              >
                {s || "All"}
              </button>
            ))}
          </div>
        </section>

        {/* Posts list */}
        <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
          ) : posts.items.length === 0 ? (
            <div className="py-16 text-center" data-testid="admin-blog-empty">
              <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"><FileText className="h-6 w-6" /></div>
              <div className="mt-3 font-semibold text-slate-700">No posts yet</div>
              <button onClick={create} className="mt-2 text-sm font-semibold text-emerald-700">Create your first post →</button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="admin-blog-list">
              {posts.items.map((p) => (
                <li key={p.id} className="px-4 sm:px-5 py-4 flex items-start gap-3 hover:bg-slate-50/60 transition-colors" data-testid={`admin-blog-row-${p.slug}`}>
                  <div className="h-14 w-20 rounded-lg overflow-hidden bg-gradient-to-br from-emerald-100 to-blue-100 ring-1 ring-slate-200 flex-shrink-0">
                    {p.cover_image?.url ? <img src={absoluteUrl(p.cover_image.url)} alt="" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider ring-1 ${STATUS_STYLES[p.status] || ""}`}>{p.status}</span>
                      {p.featured && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">Featured</span>}
                      {p.category && <span className="text-[11px] text-slate-500">{p.category.name}</span>}
                    </div>
                    <Link to={`/admin/blog/${p.id}/edit`} className="mt-1 block font-bold text-slate-900 hover:text-emerald-700 truncate text-[15px]">{p.title}</Link>
                    <div className="mt-0.5 text-[12px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="font-mono">/{p.slug}</span>
                      <span>Updated {fmtDate(p.updated_at)}</span>
                      {p.published_at && <span>Published {fmtDate(p.published_at)}</span>}
                      <span>{p.views || 0} views</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="Preview" className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid={`blog-preview-${p.slug}`}><Eye className="h-4 w-4" /></a>
                    {p.status !== "published" ? (
                      <button title="Publish" onClick={() => doPublish(p.id)} className="h-8 w-8 rounded-lg hover:bg-emerald-50 text-emerald-700 flex items-center justify-center" data-testid={`blog-publish-${p.slug}`}><Eye className="h-4 w-4" /></button>
                    ) : (
                      <button title="Unpublish" onClick={() => doUnpublish(p.id)} className="h-8 w-8 rounded-lg hover:bg-amber-50 text-amber-700 flex items-center justify-center" data-testid={`blog-unpublish-${p.slug}`}><EyeOff className="h-4 w-4" /></button>
                    )}
                    <button title="Duplicate" onClick={() => doDuplicate(p.id)} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-600 flex items-center justify-center" data-testid={`blog-duplicate-${p.slug}`}><Copy className="h-4 w-4" /></button>
                    <button title="Archive" onClick={() => doArchive(p.id)} className="h-8 w-8 rounded-lg hover:bg-amber-50 text-amber-700 flex items-center justify-center" data-testid={`blog-archive-${p.slug}`}><Archive className="h-4 w-4" /></button>
                    <button title="Delete" onClick={() => doDelete(p.id, p.title)} className="h-8 w-8 rounded-lg hover:bg-rose-50 text-rose-600 flex items-center justify-center" data-testid={`blog-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {posts.total_pages > 1 && (
            <div className="px-4 sm:px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
              <span>{posts.total} posts</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Prev</Button>
                <span>Page {page} / {posts.total_pages}</span>
                <Button size="sm" variant="outline" disabled={page >= posts.total_pages} onClick={() => update({ page: String(page + 1) })}>Next</Button>
              </div>
            </div>
          )}
        </section>

        {/* Popular + search keywords */}
        {analytics && (analytics.popular_posts?.length > 0 || analytics.top_search_keywords?.length > 0) && (
          <section className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="admin-blog-popular">
              <h3 className="font-bold text-slate-900">Popular posts</h3>
              <ul className="mt-3 space-y-2">
                {analytics.popular_posts.slice(0, 8).map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-sm">
                    <Link to={`/admin/blog/${p.id}/edit`} className="truncate text-slate-700 hover:text-emerald-700 font-medium">{p.title}</Link>
                    <span className="text-xs text-slate-500 ml-3 tabular-nums">{p.views} views</span>
                  </li>
                ))}
                {analytics.popular_posts.length === 0 && <li className="text-sm text-slate-500">No data yet.</li>}
              </ul>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="admin-blog-keywords">
              <h3 className="font-bold text-slate-900">Top search keywords (30d)</h3>
              <ul className="mt-3 space-y-2">
                {analytics.top_search_keywords.map((k) => (
                  <li key={k.q} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{k.q}</span>
                    <span className="text-xs text-slate-500 ml-3 tabular-nums">{k.count}</span>
                  </li>
                ))}
                {analytics.top_search_keywords.length === 0 && <li className="text-sm text-slate-500">No searches yet.</li>}
              </ul>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
